import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Search,
  Trash2,
  Monitor,
  Globe,
  User,
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(20);
  const [filter, setFilter] = useState('all'); // 'all', 'success', 'failed'
  const [emailSearch, setEmailSearch] = useState('');
  const [clearing, setClearing] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      
      if (filter === 'success') params.append('success_only', 'true');
      if (filter === 'failed') params.append('success_only', 'false');
      if (emailSearch) params.append('email_filter', emailSearch);
      
      const res = await axios.get(`${API}/admin/login-logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors du chargement des logs'));
    } finally {
      setLoading(false);
    }
  }, [offset, limit, filter, emailSearch]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/login-logs/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleClearLogs = async (days) => {
    if (!window.confirm(`Supprimer tous les logs de plus de ${days} jours ?`)) return;
    
    setClearing(true);
    try {
      const res = await axios.delete(`${API}/admin/login-logs?older_than_days=${days}`);
      toast.success(res.data.message);
      fetchLogs();
      fetchStats();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors de la suppression'));
    } finally {
      setClearing(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const parseUserAgent = (ua) => {
    if (!ua || ua === 'Unknown') return { browser: 'Inconnu', os: 'Inconnu' };
    
    let browser = 'Autre';
    let os = 'Autre';
    
    // Browser detection
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
    
    // OS detection
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    return { browser, os };
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Logs de Connexion
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Historique des tentatives de connexion au système
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchLogs(); fetchStats(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Connexions réussies</span>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.total_success}</div>
              <div className="text-xs text-muted-foreground">
                24h: {stats.success_24h} | 7j: {stats.success_7d}
              </div>
            </div>
          </div>

          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <XCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Échecs</span>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-red-700 dark:text-red-400">{stats.total_failed}</div>
              <div className="text-xs text-muted-foreground">
                24h: {stats.failed_24h} | 7j: {stats.failed_7d}
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <Globe className="h-5 w-5" />
              <span className="text-sm font-medium">IPs suspectes</span>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">{stats.unique_failed_ips}</div>
              <div className="text-xs text-muted-foreground">IPs avec échecs</div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
              <Clock className="h-5 w-5" />
              <span className="text-sm font-medium">Total logs</span>
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{total}</div>
              <div className="text-xs text-muted-foreground">Entrées enregistrées</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Failed Attempts Warning */}
      {stats?.recent_failed?.length > 0 && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-2">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">Derniers échecs de connexion</span>
          </div>
          <div className="space-y-1 text-sm">
            {stats.recent_failed.map((log, i) => (
              <div key={`${log.timestamp}-${log.email}-${log.ip_address}`} className="flex items-center gap-4 text-muted-foreground">
                <span className="font-mono text-xs">{formatDate(log.timestamp)}</span>
                <span>{log.email}</span>
                <span className="text-red-600">{log.reason}</span>
                <span className="text-xs">{log.ip_address}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => { setFilter('all'); setOffset(0); }}
          >
            Tous
          </Button>
          <Button 
            variant={filter === 'success' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => { setFilter('success'); setOffset(0); }}
            className={filter === 'success' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Réussis
          </Button>
          <Button 
            variant={filter === 'failed' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => { setFilter('failed'); setOffset(0); }}
            className={filter === 'failed' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            <XCircle className="h-4 w-4 mr-1" />
            Échecs
          </Button>
        </div>
        
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par email..."
            value={emailSearch}
            onChange={(e) => { setEmailSearch(e.target.value); setOffset(0); }}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleClearLogs(30)}
            disabled={clearing}
          >
            {clearing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Purger &gt;30j
          </Button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Date/Heure</th>
                <th className="text-left p-3 font-medium">Statut</th>
                <th className="text-left p-3 font-medium">Utilisateur</th>
                <th className="text-left p-3 font-medium">Email</th>
                <th className="text-left p-3 font-medium">IP</th>
                <th className="text-left p-3 font-medium">Navigateur</th>
                <th className="text-left p-3 font-medium">Raison</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    Aucun log de connexion
                  </td>
                </tr>
              ) : (
                logs.map((log, i) => {
                  const { browser, os } = parseUserAgent(log.user_agent);
                  return (
                    <tr key={log.id || `${log.timestamp}-${log.email}-${log.ip_address}-${i}`} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-mono text-xs whitespace-nowrap">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="p-3">
                        {log.success ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs">
                            <CheckCircle className="h-3 w-3" />
                            Succès
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs">
                            <XCircle className="h-3 w-3" />
                            Échec
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {log.user_name ? (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{log.user_name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 font-mono text-xs">{log.email}</td>
                      <td className="p-3 font-mono text-xs">{log.ip_address}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 text-xs">
                          <Monitor className="h-3 w-3 text-muted-foreground" />
                          <span>{browser}</span>
                          <span className="text-muted-foreground">/</span>
                          <span>{os}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        {log.reason ? (
                          <span className="text-red-600 text-xs">{log.reason}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {currentPage} sur {totalPages} ({total} entrées)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
            >
              Suivant
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
